import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { actions } from '~/redux/auth/slice';
import { actions as app } from '~/redux/app/slice';

import type { RoleSlug, User } from '~/types/models';

interface IAuthHook {
  hasAllRoles: (roles: RoleSlug[]) => boolean;
  hasAnyRole: (roles: RoleSlug[]) => boolean;
  hasRole: (role: RoleSlug, groupId?: string) => boolean;
  hasRoleGlobally: (role: RoleSlug) => boolean;
  user: User | null;
}
export const useAuth = (): IAuthHook => {
  const dispatch = useAppDispatch();
  const loaded = useAppSelector((state) => state.auth.loaded);
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    if (!user?.id || !loaded) {
      dispatch(actions.fetchUser());
      dispatch(app.fetchBackendVersion());
    }
  }, []);

  const hasRole = (role: RoleSlug, groupId?: string): boolean => {
    if (!user) {
      return false;
    }

    return (
      hasRoleGlobally(role) ||
      !!user?.userGroups?.find(
        (ug) =>
          !!(ug.groupId === groupId || groupId === undefined) && !!ug.roles?.find((ugRole) => role === ugRole.slug),
      )
    );
  };

  const hasRoleGlobally = (role: RoleSlug): boolean => {
    if (!user) {
      return false;
    }

    return !!user.roles?.find((r) => r.slug === role);
  };

  const hasAllRoles = (roles: RoleSlug[]): boolean => {
    return !roles.find((r) => !hasRole(r));
  };

  const hasAnyRole = (roles: RoleSlug[]): boolean => {
    return !!roles.find((r) => hasRole(r));
  };

  return {
    hasAllRoles,
    hasAnyRole,
    hasRole,
    hasRoleGlobally,
    user,
  };
};
